import { createSlice } from '@reduxjs/toolkit'
import { login, signup, confirm, resetPassword } from '_services/auth'
import type { RootState } from 'store/store'
import jwt_decode from "jwt-decode";
import { getInfos } from '_services/user';
import { withBasePath } from '_config/basePath';

export interface User {
  email: string;
  firstname: string;
  newsletter: boolean;
  lastname: string | null;
  level: string | null;
  field: string | null;
  university: string | null;
  notif_freq: number;
  prof_email: string | null;
  is_confirmed: boolean;
}

type AuthState = {
  userId: string | null;
  token: string | null;
  isProf: boolean | null;
  user: User | null;
  user_type: number | null;
}

export type JwtPayload = {
  sub: string
  is_prof: boolean
  exp: number
  user_type: number
}

// Try to restore session from localStorage (legacy) or from claims
const token = localStorage.getItem("token");
const claims = localStorage.getItem("auth_claims");
const parsedClaims = claims ? JSON.parse(claims) : null;

const initialState: AuthState = {
  userId: parsedClaims?.sub ?? (token ? jwt_decode<JwtPayload>(token)?.sub : null),
  isProf: parsedClaims ? parsedClaims.user_type === 2 : (token ? jwt_decode<JwtPayload>(token)?.user_type == 2 : null),
  user_type: parsedClaims?.user_type ?? (token ? jwt_decode<JwtPayload>(token)?.user_type : null),
  token: token || (parsedClaims ? 'cookie' : null),
  user: null
}

const store_token = (state, action) => {
  const { token, claims } = action.payload;
  if (claims) {
    // New httpOnly cookie flow — store only claims
    state.token = 'cookie'; // Marker that we're authenticated via cookie
    state.userId = claims.sub;
    state.isProf = claims.user_type === 2;
    state.user_type = claims.user_type;
    localStorage.setItem("auth_claims", JSON.stringify(claims));
    localStorage.removeItem("token"); // Clean up legacy token
  } else if (token) {
    // Legacy fallback
    state.token = token;
    state.userId = jwt_decode<JwtPayload>(token)?.sub;
    state.isProf = jwt_decode<JwtPayload>(token)?.user_type == 2;
    state.user_type = jwt_decode<JwtPayload>(token)?.user_type;
    localStorage.setItem("token", token);
  }
}

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authLogout: (state) => {
      localStorage.removeItem("token");
      localStorage.removeItem("auth_claims");
      localStorage.removeItem("language");
      state.token = null;
      state.userId = null;
      state.isProf = null;
      state.user_type = null;
      state.user = null;
      sessionStorage.clear();
      // Call the logout endpoint to clear the httpOnly cookie
      fetch(withBasePath('/api/auth/logout'), { method: 'POST', credentials: 'include' });
    },
    userSet: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    authSetToken: (state, action) => {
      const { token, claims } = action.payload;
      if (claims) {
        state.token = 'cookie';
        state.userId = claims.sub;
        state.isProf = claims.user_type === 2;
        state.user_type = claims.user_type;
        localStorage.setItem("auth_claims", JSON.stringify(claims));
        localStorage.removeItem("token");
      } else if (token) {
        state.token = token;
        state.userId = jwt_decode<JwtPayload>(token)?.sub;
        state.isProf = jwt_decode<JwtPayload>(token)?.user_type == 2;
        state.user_type = jwt_decode<JwtPayload>(token)?.user_type;
        localStorage.setItem("token", token);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(login.matchFulfilled, store_token)
      .addMatcher(signup.matchFulfilled, store_token)
      .addMatcher(confirm.matchFulfilled, store_token)
      .addMatcher(resetPassword.matchFulfilled, store_token)
      .addMatcher(getInfos.matchFulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addMatcher(
        (action: any) => action.type.endsWith('/rejected') &&
          action.meta?.arg?.type &&
          action.type.startsWith('authApi/') === false &&
          action.payload?.status === 401,
        (state) => {
          // Cached claims are not proof of a valid server session.
          localStorage.removeItem("token");
          localStorage.removeItem("auth_claims");
          state.token = null;
          state.userId = null;
          state.isProf = null;
          state.user_type = null;
          state.user = null;
        }
      )
  }
})


export default slice.reducer

export const selectCurrentUserId = (state: RootState) => state.auth.userId

export const logout = (dispatch) => dispatch(slice.actions.authLogout());

export const { userSet, authSetToken } = slice.actions;
